import { useState } from "react";
import { Clock, ArrowRight, X } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { useUpgradeModal } from "../context/UpgradeModalContext";

const DISMISS_KEY = "ezitemizer:trial-banner-dismissed";

function getTodayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

function daysUntil(unixSecOrIso) {
  if (!unixSecOrIso) return null;
  const ts =
    typeof unixSecOrIso === "number"
      ? unixSecOrIso * 1000
      : Date.parse(unixSecOrIso);
  if (!ts || Number.isNaN(ts)) return null;
  const ms = ts - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

export default function TrialBanner() {
  const { user } = useAuth();
  const { openUpgradeModal } = useUpgradeModal();

  // Hide if dismissed today (per-day key so it returns the next morning)
  const todayKey = getTodayKey();
  const [dismissed, setDismissed] = useState(
    typeof window !== "undefined" &&
      localStorage.getItem(DISMISS_KEY) === todayKey
  );

  const plan = user?.plan;
  if (!plan || plan.is_demo) return null;
  if (plan.subscription_status !== "trialing") return null;

  const days = daysUntil(plan.trial_end);
  if (days == null || days < 0) return null;
  if (dismissed) return null;

  // Tone the banner: amber for last-3-days urgency, neutral navy otherwise
  const urgent = days <= 3;
  const label = days === 0 ? "today" : days === 1 ? "tomorrow" : `in ${days} days`;
  const planLabel = plan.label?.replace("ezItemizer ", "") || "your";

  const onDismiss = () => {
    localStorage.setItem(DISMISS_KEY, todayKey);
    setDismissed(true);
  };

  return (
    <div
      data-testid="trial-banner"
      className={`flex items-center justify-between gap-3 px-4 sm:px-6 py-2.5 border-b ${
        urgent
          ? "bg-[#FFF1E0] border-[#FF9800] text-[#7A3E00]"
          : "bg-[#1A2332] border-[#243044] text-zinc-100"
      }`}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <Clock
          size={16}
          weight={urgent ? "fill" : "duotone"}
          className={urgent ? "text-[#FF9800]" : "text-[#FF9800]"}
        />
        <span className="text-xs sm:text-sm">
          <span className="font-bold uppercase tracking-widest text-[10px] mr-2">
            {urgent ? "Trial ending soon" : "Free trial"}
          </span>
          <span className="hidden sm:inline">
            Your <span className="font-semibold">{planLabel}</span> trial ends{" "}
          </span>
          <span className="sm:hidden">Ends </span>
          <span className="font-bold">{label}</span>
          <span className="hidden md:inline">
            {" "}— add a card or change plan to keep access.
          </span>
        </span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={() =>
            openUpgradeModal({
              reason: "trial_ending",
              message:
                days === 0
                  ? "Your free trial ends today."
                  : `Your free trial ends ${label}.`,
              currentTier: plan.tier,
            })
          }
          data-testid="trial-banner-cta"
          className={`inline-flex items-center gap-1.5 font-bold text-[10px] uppercase tracking-widest px-3 py-1.5 transition-colors ${
            urgent
              ? "bg-[#FF9800] hover:bg-[#E68900] text-[#1A2332]"
              : "bg-[#FF9800] hover:bg-[#E68900] text-[#1A2332]"
          }`}
        >
          Manage plan
          <ArrowRight size={11} weight="bold" />
        </button>
        <button
          onClick={onDismiss}
          data-testid="trial-banner-dismiss"
          aria-label="Dismiss for today"
          className={`p-1 transition-colors ${
            urgent
              ? "hover:bg-[#FF9800]/20 text-[#7A3E00]"
              : "hover:bg-white/10 text-zinc-400"
          }`}
        >
          <X size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}
